import type { ParticipantRole } from '../realtime/frames';
import type { ParticipantTile, ScreenShareTile } from './roomState';

/**
 * Webinar-mode derivation (ADR 0010): the control service knows the room kind, but neither
 * the join response nor the WS state frame carries it on the wire (change request filed), so
 * the client derives the viewer experience from what it verifiably has:
 *
 *  - the own join JWT's LiveKit video grant: webinar non-hosts are minted
 *    subscribe-only tokens (canPublish=false, internal/livekit/token.go) while every meeting
 *    member and every webinar host carries canPublish=true;
 *  - the room's Q&A board: the handshake replays qa_history in webinar rooms only,
 *    so its arrival proves the kind even for a publishing host.
 *
 * The token payload is decoded without signature verification - it is never a trust decision,
 * only a UI-layout hint; the SFU enforces the real grants.
 */

/** The video-grant slice of a LiveKit join token payload the layout cares about. */
export interface TokenVideoGrants {
  /** true/false from the token's video.canPublish claim; null when the claim is absent. */
  readonly canPublish: boolean | null;
}

function base64UrlDecode(segment: string): string | null {
  try {
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return atob(padded);
  } catch {
    return null;
  }
}

/**
 * Decodes the join JWT's payload and narrows its video grant slice. Returns null for a
 * malformed token (wrong segment count, non-JSON payload) - the caller falls back to the
 * other kind signals instead of guessing.
 */
export function decodeTokenVideoGrants(token: string): TokenVideoGrants | null {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }
  const payload = base64UrlDecode(parts[1] ?? '');
  if (payload === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload) as unknown;
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }
  const video = (parsed as Record<string, unknown>).video;
  if (typeof video !== 'object' || video === null) {
    return { canPublish: null };
  }
  const canPublish = (video as Record<string, unknown>).canPublish;
  return { canPublish: typeof canPublish === 'boolean' ? canPublish : null };
}

/**
 * True when the token mints its holder a webinar viewer: an explicit canPublish=false is the
 * subscribe-only viewer shape. An absent claim reads as a publisher (meeting semantics).
 */
export function isViewerJoinToken(token: string): boolean {
  return decodeTokenVideoGrants(token)?.canPublish === false;
}

/** Inputs of the webinar scene derivation: the rendered tiles and the live presence roles. */
export interface WebinarSceneInput {
  readonly tiles: readonly ParticipantTile[];
  readonly shares: readonly ScreenShareTile[];
  /** Live roles by identity, for preferring the host tile on the camera stage. */
  readonly roles: ReadonlyMap<string, ParticipantRole>;
  /**
   * True while the local participant is publish-capable: staff by role, a granted viewer by
   * their minted token. A publish-capable local camera joins the stage too.
   */
  readonly ownPublishing: boolean;
}

/**
 * The camera-stage line-up: every participant with a live camera first (publishers - staff,
 * then anyone speaking by grant), ordered hosts-before-others and stable by identity; the
 * locally publishing camera joins the stage as well. Camera-off viewers never render a dark
 * tile in webinar mode - the stage is the broadcast, not the roster.
 */
export function webinarStageTiles(input: WebinarSceneInput): readonly ParticipantTile[] {
  const rank = (tile: ParticipantTile): number => {
    if (!tile.isLocal) {
      return input.roles.get(tile.identity) === 'admin' ? 0 : 1;
    }
    return input.ownPublishing ? 1 : 2;
  };
  return input.tiles
    .filter((tile) =>
      tile.isLocal ? input.ownPublishing && tile.videoTrack !== null : tile.videoTrack !== null,
    )
    .sort((a, b) => {
      if (rank(a) !== rank(b)) {
        return rank(a) - rank(b);
      }
      return a.identity.localeCompare(b.identity);
    });
}

/** Viewers count for the top bar: connected participants carrying the plain participant role. */
export function viewersCount(
  participants: ReadonlyMap<string, { readonly role: ParticipantRole }>,
): number {
  let count = 0;
  for (const participant of participants.values()) {
    if (participant.role === 'participant') {
      count += 1;
    }
  }
  return count;
}
